/**
 * TrackingModePicker
 * 
 * Uses SegmentedControl (from 0C) — NOT raw ToggleButtonGroup.
 * Options derived from customer's trackingMode via useCapability:
 *   manual customer → single option "Manual" (pre-selected, other disabled)
 *   beacon customer → single option "Beacon" (pre-selected, other disabled)
 *   hybrid customer → both "Beacon" and "Manual" selectable
 * 
 * LAT-16: tracking mode picker respects customer capability
 * 
 * Props:
 *   value: string (current tracking mode)
 *   onChange: (value) => void
 *   size: 'small' | 'medium'
 *   capability: (optional) capability object; when provided, overrides useCapability's
 *     read of the current activeCustomer. Used by ProjectSetupWizard (R-18) where the
 *     wizard admin can pick a customer different from the store's active one.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import SegmentedControl from './SegmentedControl';
import { useCapability } from '../../hooks/useCapability';

export default function TrackingModePicker({
  value,
  onChange,
  size = 'medium',
  label = 'Tracking Mode',
  capability: capabilityProp,
}) {
  const capabilityFromStore = useCapability();
  const capability = capabilityProp || capabilityFromStore;

  const options = [
    {
      value: 'beacon',
      label: 'Beacon',
      disabled: capability.isManualOnly,
      tooltip: capability.isManualOnly
        ? 'Beacon tracking is not available for this customer'
        : undefined,
    },
    {
      value: 'manual',
      label: 'Manual',
      disabled: capability.isBeaconOnly,
      tooltip: capability.isBeaconOnly
        ? 'Manual tracking is not available for this customer'
        : undefined,
    },
  ];

  // If hybrid, also allow hybrid option
  if (capability.isHybrid) {
    options.push({
      value: 'hybrid',
      label: 'Both',
    });
  }

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
      )}
      <SegmentedControl
        options={options}
        value={value}
        onChange={onChange}
        size={size}
      />
    </Box>
  );
}
